import type { ReadField } from '@ns/plugin-picker-shared';
import { unknownFilterField, unknownValue } from '@ns/plugin-picker-shared';

import { type FieldSpec, isPickerValue } from './field-spec';
import { kqlStringLiteral, kqlStringLiteralList } from './kql-literal';
import type { MatchValue } from './match';
import { MatchAuthorError, renderMatch, resolveMatch } from './match';
import type { Answer, Selected } from './page';
import { selectedFrom } from './page';
import type { AzureSubscription } from './types';

/**
 * The api-version of every Resource Graph call these pickers make.
 *
 * @remarks
 * Fixed at layer 4 and never reachable from a template. A
 * picker makes assumptions about the shape of what it returns, and an author
 * who could move the version would move the ground under those assumptions.
 */
export const AZURE_PICKER_API_VERSION = '2024-04-01';

/**
 * The subscription states a template can still create something in.
 *
 * @remarks
 * Microsoft: `Enabled` is "You can use the subscription to deploy new
 * resources" and `PastDue` is "Your subscription is still active ... All
 * operations are available". `Warned`, `Disabled` and `Deleted` cannot. This is
 * OUR choice, not a property of Azure, which is why it lives here and not in
 * layer 3.
 */
export const CREATABLE_SUBSCRIPTION_STATES: readonly string[] = [
  'Enabled',
  'PastDue',
];

/** The five states Microsoft defines, in the order their documentation lists them. */
const AZURE_SUBSCRIPTION_STATES: readonly string[] = [
  'Enabled',
  'PastDue',
  'Warned',
  'Disabled',
  'Deleted',
];

/**
 * The three environments, which are OURS: Azure has no such field.
 *
 * @remarks
 * The fixed part of the query derives them from the subscription name, so this
 * list and that `case` say the same thing and have to stay in step (`SBQ`).
 */
const SUBSCRIPTION_ENVIRONMENTS: readonly string[] = [
  'development',
  'staging',
  'production',
];

/** The keys `filter` offers, and no others. */
const SUBSCRIPTION_FILTER_FIELDS: readonly string[] = [
  'state',
  'environment',
  'tags',
];

/** What a template author may narrow the subscription list with. */
export interface SubscriptionFilter {
  environment?: readonly string[];
  state?: readonly string[];
  tags?: Readonly<Record<string, MatchValue>>;
}

/** The `ui:options` this picker offers: exactly one key. */
export interface SubscriptionPickerUiOptions {
  filter?: SubscriptionFilter;
}

/**
 * The fixed part of the query.
 *
 * @remarks
 * `environment` is derived here and not after fetching, because the result
 * carries the field even when nothing filters on it. A filter
 * cannot point at a column the `project` only creates later, so a filter on a
 * provider field uses the full path (`tostring(properties.state)`) and only
 * `environment` may go by name, because the `extend` above makes it.
 */
const SUBSCRIPTION_HEAD = [
  'resourcecontainers',
  '| where type =~ "microsoft.resources/subscriptions"',
  '| extend environment = case(',
  '    name startswith_cs "DEV-",   "development",',
  '    name endswith_cs "-STAGING", "staging",',
  '    name endswith_cs "-PROD",    "production",',
  '    "development")',
].join('\n');

/** The fields of {@link AzureSubscription}, one per column the tail below projects. */
export const SUBSCRIPTION_FIELDS = {
  environment: 'string',
  id: 'string',
  name: 'string',
  state: 'string',
  subscriptionId: 'string',
  tags: 'optional',
  type: 'string',
} as const satisfies FieldSpec<AzureSubscription>;

const SUBSCRIPTION_TAIL = [
  '| project id, subscriptionId, name, state = tostring(properties.state), tags, environment, type',
  '| order by tolower(name) asc // on the visible text, case-insensitive',
].join('\n');

/**
 * Builds the KQL for the subscription picker.
 *
 * @param filter - What the author wrote under `ui:options.filter`.
 * @returns The full query text.
 */
export function buildSubscriptionQuery(
  readField: ReadField,
  filter?: SubscriptionFilter,
): string {
  const clauses: string[] = [];

  // Both fields have a fixed vocabulary, so a value outside it can never match
  // and is an author error rather than an empty list.
  // An unoffered key is one too; the test is what THIS picker offers,
  // not what Azure knows, because `environment` is ours and `location` is not.
  for (const key of Object.keys(filter ?? {})) {
    if (!SUBSCRIPTION_FILTER_FIELDS.includes(key)) {
      throw new MatchAuthorError(
        unknownFilterField(key, SUBSCRIPTION_FILTER_FIELDS),
      );
    }
  }
  // Both fields have a closed vocabulary, and neither goes through
  // `resolveMatch`, so the empty-list check has to happen here as well.
  for (const [field, written, valid] of [
    ['subscription state', filter?.state, AZURE_SUBSCRIPTION_STATES],
    ['environment', filter?.environment, SUBSCRIPTION_ENVIRONMENTS],
  ] as const) {
    if (written?.length === 0) {
      throw new MatchAuthorError(
        `An empty list can never match anything. Remove the ${field} filter, or name the values it should accept.`,
      );
    }
    const unknown = written?.find(value => !valid.includes(value));
    if (unknown !== undefined) {
      throw new MatchAuthorError(unknownValue(field, unknown, valid));
    }
  }

  // Without `filter.state` the picker supplies the two creatable states
  // itself. This is the only value layer 4 invents.
  const states = filter?.state ?? CREATABLE_SUBSCRIPTION_STATES;
  clauses.push(
    `| where tostring(properties.state) in~ ${kqlStringLiteralList(states)}`,
  );

  if (filter?.environment !== undefined) {
    clauses.push(
      `| where environment in~ ${kqlStringLiteralList(filter.environment)}`,
    );
  }

  // One `where` per tag key, each value compared the way `resolveMatch` says.
  // `state` and `environment` above stay on `in~` instead: both have a closed
  // word list, and a value outside it is already an author error, so every
  // valid value is exactly one term and no operator choice is left to make.
  for (const [key, value] of Object.entries(filter?.tags ?? {})) {
    // `undefined` means the reference is still empty, so this comparison drops
    // rather than narrowing the list to nothing.
    const match = resolveMatch(value, readField);
    if (match === undefined) continue;
    const column = `tostring(tags[${kqlStringLiteral(key)}])`;
    clauses.push(`| where ${renderMatch(column, match)}`);
  }

  return [SUBSCRIPTION_HEAD, ...clauses, SUBSCRIPTION_TAIL].join('\n');
}

/**
 * Whether the picker may fetch.
 *
 * @remarks
 * This picker depends on no other field, so it is always allowed.
 */
export function subscriptionQueryEnabled(): boolean {
  return true;
}

/**
 * The rows of a Resource Graph answer, narrowed to what this picker guarantees.
 *
 * @remarks
 * A narrowing of an already-validated row, not a second validation: the
 * fields come straight out of the `project` above.
 */
export function selectSubscriptions(
  answer: Answer,
): Selected<AzureSubscription> {
  return selectedFrom(answer, isAzureSubscription);
}

function isAzureSubscription(row: unknown): row is AzureSubscription {
  return isPickerValue<AzureSubscription>(row, SUBSCRIPTION_FIELDS);
}
